import { useState } from 'react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ProcessorEditHeader } from '../../components/processor-edit-header';
import { Textarea } from '@/components/ui/textarea';

import { ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { ErrorDisplay } from '@/components/ui/error-display';
import { parseTRPCError } from '@/lib/utils/trpc-error-utils';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import { SecretInput } from '@/components/form/secret-input';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';

interface KessPayEditPageProps {
  processor: ProcessorDetails;
}

const KessPayEditPageContainerInner = () => {
  const { t } = useTranslation('processors');
  const id = useProcessorEditId();
  const { data, isLoading, error } = useProcessorById(id!);

  if (isLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={parseTRPCError(error)} />;
  }

  if (!data) {
    return <div className="container mx-auto py-8">{t('processorNotFound')}</div>;
  }

  return <KessPayEditPage processor={data} />;
};

const KessPayEditPage = ({ processor }: KessPayEditPageProps) => {
  const { t } = useTranslation('processors');
  const navigate = useNavigate();
  const processorExit = useProcessorEditExit();
  const { toast } = useToast();

  const [validationError, setValidationError] = useState<string | null>(null);

  const updateProcessorMutation = useUpdateProcessor();

  const [formData, setFormData] = useState({
    name: processor.name || '',
    enabled: processor.enabled ?? true,
    testMode: (processor.options as any)?.testMode ?? false,
    dryMode: (processor.options as any)?.dryMode === true,
    clientId: (processor.options as any)?.clientId || '',
    clientSecret: (processor.options as any)?.clientSecret || '',
    username: (processor.options as any)?.username || '',
    password: (processor.options as any)?.password || '',
    sellerCode: (processor.options as any)?.sellerCode || '',
    apiSecretKey: (processor.options as any)?.apiSecretKey || '',
    publicKey: (processor.options as any)?.publicKey || '',
  });

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.clientId) {
      setValidationError(t('kesspay.credentials.clientIdRequired'));
      return;
    }

    if (!formData.clientSecret) {
      setValidationError(t('kesspay.credentials.clientSecretRequired'));
      return;
    }

    if (!formData.username) {
      setValidationError(t('kesspay.credentials.usernameRequired'));
      return;
    }

    if (!formData.password) {
      setValidationError(t('kesspay.credentials.passwordRequired'));
      return;
    }

    if (!formData.sellerCode) {
      setValidationError(t('kesspay.kesspaySettings.sellerCodeRequired'));
      return;
    }

    if (!formData.apiSecretKey) {
      setValidationError(t('kesspay.kesspaySettings.apiSecretKeyRequired'));
      return;
    }

    if (!formData.publicKey) {
      setValidationError(t('kesspay.kesspaySettings.publicKeyRequired'));
      return;
    }

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: 'kesspay',
          name: formData.name,
          enabled: formData.enabled,
          options: {
            testMode: formData.testMode,
            dryMode: formData.dryMode,
            clientId: formData.clientId,
            clientSecret: formData.clientSecret,
            username: formData.username,
            password: formData.password,
            sellerCode: formData.sellerCode,
            apiSecretKey: formData.apiSecretKey,
            publicKey: formData.publicKey,
          },
        } as any,
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch (error) {
      console.error('Error saving processor:', error);
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('kesspay.editTitle')}
        onCancel={() => (processorExit.isEmbed ? processorExit.exitCancel() : navigate(-1))}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        {/* General Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('enterProcessorName')}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        {/* Test Mode Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            {formData.testMode && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <p className="text-sm text-yellow-800">
                  ⚠️ {t('kesspay.testMode.description')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* OAuth Credentials */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('kesspay.credentials.oauthTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <SecretInput
                  label={t('kesspay.credentials.clientIdLabel')}
                  value={formData.clientId}
                  onChange={(value) => setFormData({ ...formData, clientId: value })}
                  placeholder={t('kesspay.credentials.clientIdPlaceholder')}
                />
              </div>

              <div>
                <SecretInput
                  label={t('kesspay.credentials.clientSecretLabel')}
                  value={formData.clientSecret}
                  onChange={(value) => setFormData({ ...formData, clientSecret: value })}
                  placeholder={t('kesspay.credentials.clientSecretPlaceholder')}
                />
              </div>

              <div>
                <Label htmlFor="username">{t('kesspay.credentials.usernameLabel')} *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder={t('kesspay.credentials.usernamePlaceholder')}
                />
              </div>

              <div>
                <SecretInput
                  label={t('kesspay.credentials.passwordLabel')}
                  value={formData.password}
                  onChange={(value) => setFormData({ ...formData, password: value })}
                  placeholder={t('kesspay.credentials.passwordPlaceholder')}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* KessPay Settings */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{t('kesspay.kesspaySettings.settingsTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="sellerCode">{t('kesspay.kesspaySettings.sellerCodeLabel')} *</Label>
              <Input
                id="sellerCode"
                value={formData.sellerCode}
                onChange={(e) => setFormData({ ...formData, sellerCode: e.target.value })}
                placeholder={t('kesspay.kesspaySettings.sellerCodePlaceholder')}
              />
            </div>

            <div>
              <SecretInput
                label={t('kesspay.kesspaySettings.apiSecretKeyLabel')}
                value={formData.apiSecretKey}
                onChange={(value) => setFormData({ ...formData, apiSecretKey: value })}
                placeholder={t('kesspay.kesspaySettings.apiSecretKeyPlaceholder')}
              />
            </div>

            <div>
              <Label htmlFor="publicKey">{t('kesspay.kesspaySettings.rsaPublicKeyLabel')} *</Label>
              <Textarea
                id="publicKey"
                value={formData.publicKey}
                onChange={(e) => setFormData({ ...formData, publicKey: e.target.value })}
                placeholder={t('kesspay.kesspaySettings.rsaPublicKeyPlaceholder')}
                rows={6}
                className="font-mono text-sm"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('kesspay.kesspaySettings.rsaPublicKeyDescription')}
              </p>
            </div>
          </CardContent>
        </Card>

                <ProcessorDryModeField
                    checked={formData.dryMode}
                    onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
                    disabled={updateProcessorMutation.isPending}
                />
      </div>
    </div>
  );
};

export const KessPayEditPageContainer = withRequiredPermission(KessPayEditPageContainerInner, PERMISSIONS.PROCESSORS_MANAGE);
